import { FlowNode } from './types'
import { Handle, Position } from '@xyflow/react';
import '../index.css';

export const FlowNodeComponent: React.FC<{ data: FlowNode['data'] }> = ({ data }) => { 

  const { battleTested, title: label, fresh, speed } = data;
  // const normalizedBattleTested = Math.log(1 + battleTested) / Math.log(1 + 1000); // Assuming 1000 as the practical max

  // Determine border thickness based on battleTested score
  const minBorderWidth = 2; // Minimum border thickness in pixels
  const maxBorderWidth = 30; // Maximum border thickness in pixels
  const borderWidth = Math.min(
    maxBorderWidth,
    minBorderWidth + Math.floor((battleTested / 100) * (maxBorderWidth - minBorderWidth))
  );

  const a = 100; // Steepness of the logistic curve
  const b = 0.1; // Midpoint (adjust as needed)
  const alpha = 1.03; // Adjust alpha as needed, increse numerator
  const beta = .1; // Adjust beta as needed, increse numerator
  const gamma = 2; // Adjust gamma as needed, increase denomonator
  const k = 1000; // Adjust k as needed, increase denomonator

  function logisticScaling(rawOutput: number): number {
    return 1 / (1 + Math.exp(-a * (rawOutput - b)));
  }

  const flameSize =  (): number => {
    const clampedSpeed = Math.max(speed, 0);
    const clampedFresh = Math.abs(fresh);
    const tested = Math.abs(battleTested);

    const rawOutput = ((clampedSpeed ** alpha) * (clampedFresh ** beta)) / ((tested + 1) ** gamma + k);
    const normalizedOutput = logisticScaling(rawOutput);

    return normalizedOutput
  }

  // Calculate background image properties based on `fresh` score
  const minSize = 100; // Minimum image size in percentage
  const maxSize = 300; // Maximum image size in percentage
  const imageSize = minSize + ((flameSize()) * (maxSize - minSize)); // Size scaling
  const opacity = Math.min(1, Math.max(0.0001, flameSize())); // Opacity scaling

  // Determine gradient color based on battleTested score
  // Define color stops for smooth gradient transitions
  const gradient = getGradientColor(battleTested);

  // Outer div style for gradient border
  const outerStyle: React.CSSProperties = {
    position: 'relative',
    padding: `${borderWidth}px`, // Border thickness
    background: gradient, // Gradient background
    borderRadius: '8px', // Rounded corners
    boxSizing: 'border-box',
    transition: 'padding 0.3s ease, background 0.3s ease', // Smooth transitions
  };

  // Inner div style for node content
  const innerStyle: React.CSSProperties = {
    background: 'white', // Node background
    borderRadius: '6px', // Slightly smaller radius to show border
    padding: '10px', // Inner padding
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)', // Subtle shadow
    textAlign: 'center',
    transition: 'background 0.3s ease', // Smooth background transition
  };

  // Styles for the background image
  const backgroundImageStyle: React.CSSProperties = {
    content: '""',
    position: 'absolute',
    top: '-60%',
    left: '50%',
    width: `${imageSize}%`,
    height: `${imageSize}%`,
    transform: 'translate(-50%, -50%)',
    backgroundImage: 'url(/Flame.gif)',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    opacity, // Control transparency
    zIndex: -1, // Position the image behind the content
  };

  return (
    <div style={outerStyle} className="flow-node-outer">
      <div style={backgroundImageStyle} className="node-background" />
      <div style={innerStyle} className="flow-node-inner">
        <Handle type="target" position={Position.Left} />
        <div className="node-label">{label}</div>
        <Handle type="source" position={Position.Right} />
      </div>
    </div>
  );
};

/**
 * Function to determine a smooth gradient color based on battleTested score
 * @param score - The battleTested score (0-100)
 * @returns A CSS linear-gradient string
 */
function getGradientColor(score: number): string {
  // Define key colors and their corresponding score ranges
  const colors = [
    { color: 'lightgray', min: 0, max: 9 },
    { color: 'blue', min: 10, max: 21 },
    { color: 'purple', min: 22, max: 34 },
    { color: 'brown', min: 35, max: 48 },
    { color: 'black', min: 49, max: 69 },
    { color: 'red', min: 70, max: 100 },
  ];

  // Find the current color range
  const currentColorIndex = colors.findIndex((c) => score >= c.min && score <= c.max);
  const currentColor = colors[currentColorIndex];

  // Handle edge cases where score doesn't match any range
  if (!currentColor) {
    // console.error(`Invalid score: ${score}`);
    return 'transparent'; // Fallback to transparent
  }

  // Get the next color range
  const nextColor = colors[currentColorIndex + 1];

  // If at the maximum score or no next color, return a solid current color
  if (!nextColor) return currentColor.color;

  // Calculate the relative percentage between the current and next color
  const range = currentColor.max - currentColor.min;
  const progress = (score - currentColor.min) / range;

  // Generate the gradient
  return `linear-gradient(
    to right,
    ${currentColor.color} ${(1 - progress) * 100}%,
    ${nextColor.color} ${progress * 100}%
  )`;
}

